import cn from 'classnames';
import * as React from 'react';
import { useCallback } from 'react';

import styles from './styles.module.scss';

export interface TabsProps {
  className?: string;
}

const Tabs: React.FC<TabsProps> = ({ className, ...rest }) => {
  const handleClickCapture = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    const tabButton = (event.target as HTMLElement).closest('button[role="tab"]') as HTMLButtonElement | null;
    if (tabButton) {
      const tabButtons = tabButton.closest('[role="tablist"]')?.querySelectorAll('button[role="tab"]') || [];
      tabButtons.forEach(button => {
        button.setAttribute('aria-selected', 'false');
        button.setAttribute('tabIndex', '-1');
      });

      tabButton.setAttribute('aria-selected', 'true');
      tabButton.setAttribute('tabIndex', '0');

      const tabPanels = document.querySelectorAll('[role="tabpanel"]');
      tabPanels.forEach(panel => {
        panel.setAttribute('aria-hidden', 'true');
        panel.classList.remove('active');
        panel.setAttribute('tabIndex', '-1');
      });

      const tabPanelId = tabButton.getAttribute('aria-controls');

      if (tabPanelId) {
        const tabPanel = document.getElementById(tabPanelId);
        if (tabPanel) {
          tabPanel.setAttribute('aria-hidden', 'false');
          tabPanel.classList.add('active');
          tabPanel.setAttribute('tabIndex', '0');
        }
      }
    }
  }, []);

  const handleKeyDownCapture = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const tabButton = (event.target as HTMLElement).closest('button[role="tab"]') as HTMLButtonElement | null;
    if (tabButton && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) {
      event.preventDefault();
      const tabButtons = Array.from(
        tabButton.closest('[role="tablist"]')?.querySelectorAll('button[role="tab"]') || []
      );
      const currentIndex = tabButtons.indexOf(tabButton);
      let newIndex = currentIndex;

      if (event.key === 'ArrowLeft') {
        newIndex = currentIndex === 0 ? tabButtons.length - 1 : currentIndex - 1;
      } else if (event.key === 'ArrowRight') {
        newIndex = currentIndex === tabButtons.length - 1 ? 0 : currentIndex + 1;
      }

      const newTabButton = tabButtons[newIndex] as HTMLButtonElement;
      newTabButton.focus();
      newTabButton.click();
    }
    if (tabButton && (event.key === 'Home' || event.key === 'End')) {
      event.preventDefault();
      const tabButtons = Array.from(
        tabButton.closest('[role="tablist"]')?.querySelectorAll('button[role="tab"]') || []
      );
      let newIndex = 0;

      if (event.key === 'End') {
        newIndex = tabButtons.length - 1;
      }

      const newTabButton = tabButtons[newIndex] as HTMLButtonElement;
      newTabButton.focus();
      newTabButton.click();
    }
  }, []);

  return (
    <div
      onClickCapture={handleClickCapture}
      onKeyDownCapture={handleKeyDownCapture}
      className={cn(styles.tabs, className)}
      {...rest}
    />
  );
};

export default Tabs;
